/**
 * Âm báo "Minh Tiến" + đọc to việc giao (DEC-671, anh Tiến 02/11/2026: "làm âm thanh kiểu Minh Tiến để báo là có việc cần giao,
 * hoặc đọc luôn tin càng tốt. Ví dụ: Có lệnh lấy hàng, hoàn thành việc"; "Tin trên ERP ưu tiên", không đi qua Zalo).
 *
 * Chỉ các loại thông báo dưới đây mới phát âm báo / đọc to; các loại khác giữ như cũ (chuông, thông báo nổi).
 * Câu đọc = "Minh Tiến. <câu dẫn>. <tiêu đề thông báo>".
 */
export type AnnounceKind = 'assign' | 'done' | 'progress' | 'remind';

export interface AnnounceRule {
  kind: AnnounceKind;
  /** Câu dẫn đọc sau "Minh Tiến". */
  lead: string;
}

export const ANNOUNCE_RULES: Record<string, AnnounceRule> = {
  // Việc giao cho tôi
  FLEET_PICKUP_ASSIGNED: { kind: 'assign', lead: 'Có lệnh lấy hàng' },
  DELIVERY_ASSIGNED: { kind: 'assign', lead: 'Có phiếu giao hàng mới' },
  DELIVERY_AUTO_ASSIGNED: { kind: 'assign', lead: 'Có phiếu giao hàng mới' },
  DELIVERY_VEHICLE_CHANGED: { kind: 'assign', lead: 'Phiếu giao đổi xe' },
  DELIVERY_PICK: { kind: 'assign', lead: 'Soạn hàng' },
  FLEET_TASK_ASSIGNED: { kind: 'assign', lead: 'Có việc mới' },
  HR_WORK_ORDER: { kind: 'assign', lead: 'Có tin giao việc' },
  LEAD_ASSIGNED: { kind: 'assign', lead: 'Có khách mới được giao' },
  COMPLAINT_ASSIGNED: { kind: 'assign', lead: 'Có khiếu nại cần xử lý' },
  // Hoàn thành việc
  FLEET_TASK_DONE: { kind: 'done', lead: 'Hoàn thành việc' },
  DELIVERY_ENOUGH_CONFIRMED: { kind: 'done', lead: 'Phiếu đã đủ hàng' },
  // Tiến độ chuyến lấy hàng (nhận chuyến, tới, lấy xong, về kho)
  FLEET_PICKUP_PROGRESS: { kind: 'progress', lead: 'Chuyến lấy hàng' },
  FLEET_PICKUP_CARGO: { kind: 'progress', lead: 'Hàng lấy về' },
  // Lịch nhắc (anh Tiến 02/11/2026: "Lịch nhắc"; câu dẫn = đúng việc cần làm: "nhắc trả xe → đến bãi đọc Trả xe")
  FOLLOW_UP_DUE: { kind: 'remind', lead: 'Chăm sóc khách' },
  FLEET_RETURN_REMINDER: { kind: 'remind', lead: 'Trả xe' },
  FLEET_WEEKLY_CHECK: { kind: 'remind', lead: 'Kiểm xe thứ Hai' },
  FLEET_CLOSEOUT_DUE: { kind: 'remind', lead: 'Chốt dầu cuối tháng' },
  FLEET_CRANE_OPEN: { kind: 'remind', lead: 'Bấm xong cẩu' },
  FLEET_YARD_CHECK: { kind: 'remind', lead: 'Đưa xe về bãi' },
  FLEET_MAINTENANCE_DUE: { kind: 'remind', lead: 'Bảo dưỡng xe' },
  FLEET_EXPIRING: { kind: 'remind', lead: 'Gia hạn giấy tờ' },
  WORKFLOW_OVERDUE: { kind: 'remind', lead: 'Duyệt việc quá hạn' },
  COMPLAINT_OVERDUE: { kind: 'remind', lead: 'Xử lý khiếu nại quá hạn' },
  QUOTE_EXPIRING: { kind: 'remind', lead: 'Báo giá sắp hết hạn' },
  SO3_DOCS_INCOMPLETE: { kind: 'remind', lead: 'Bổ sung hồ sơ phiếu SO3' },
  DELIVERY_CONTACT_MISSING: { kind: 'remind', lead: 'Bổ sung thông tin giao' },
  HR_CHECKIN_MISSING: { kind: 'remind', lead: 'Chấm công' },
  TENDER_DEADLINE: { kind: 'remind', lead: 'Hạn dự thầu' },
};

export const ANNOUNCE_KIND_LABELS: Record<AnnounceKind, string> = {
  assign: 'Việc giao cho tôi: lệnh lấy hàng, phiếu giao, soạn hàng, việc mới',
  done: 'Hoàn thành việc: tài xế báo xong, phiếu đủ hàng',
  progress: 'Tiến độ chuyến lấy hàng: nhận chuyến, lấy xong, về kho',
  remind: 'Lịch nhắc: nhắc việc, trả xe, kiểm xe, chốt dầu, hạn báo giá, hạn thầu',
};

export const announceRuleOf = (notificationType: string): AnnounceRule | null => ANNOUNCE_RULES[notificationType] ?? null;

const MAX_SPOKEN = 260;

/** Làm sạch chữ để máy đọc: bỏ ký hiệu, biểu tượng cảm xúc; "·", "→", "|" thành dấu phẩy; cắt dài. */
export function cleanForSpeech(s: string): string {
  const t = s
    .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '')
    .replace(/\u{FE0F}/gu, '')
    .replace(/\s*[·•|→⇒]\s*/g, ', ')
    .replace(/[#*_`~<>[\]{}"“”]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/(,\s*)+/g, ', ')
    .replace(/^[,\s]+|[,\s]+$/g, '')
    .trim();
  if (t.length <= MAX_SPOKEN) return t;
  const cut = t.slice(0, MAX_SPOKEN);
  const sp = cut.lastIndexOf(' ');
  return (sp > MAX_SPOKEN * 0.6 ? cut.slice(0, sp) : cut).trim();
}

/** Người nghe (chủ máy): gọi tên + Anh / Chị theo giới tính (anh Tiến 02/11/2026: "Gọi Tên + chi tiết công việc, xưng Anh hoặc Chị theo giới tính"). */
export interface AnnounceListener {
  fullName?: string | null;
  gender?: string | null; // 'male' | 'female' | 'other' | null (hồ sơ nhân viên)
}

/** "Nguyễn Văn Nam" + male → "Anh Nam"; female → "Chị Lan"; chưa khai giới tính → "Nam"; không có tên → "". */
export function callName(l: AnnounceListener | null | undefined): string {
  const given = (l?.fullName ?? '').trim().split(/\s+/).filter(Boolean).pop() ?? '';
  if (!given) return '';
  const hon = l?.gender === 'male' ? 'Anh' : l?.gender === 'female' ? 'Chị' : '';
  return hon ? `${hon} ${given}` : given;
}

export interface SpeechOpts {
  brand?: string;
  listener?: AnnounceListener | null;
}

/**
 * Câu đọc to cho một thông báo; null nếu loại này không thuộc âm báo.
 * Anh Tiến 02/11/2026: "Nhắc đúng việc, không phải chỉ Minh Tiến", "Đọc chi tiết OK" →
 * "Minh Tiến. Anh Nam ơi, trả xe. Xe 61H00609 đã về bãi, bạn chưa bấm Trả xe. <chi tiết>."
 */
export function announceSpeech(n: { type: string; title: string; message?: string | null }, opts: SpeechOpts = {}): string | null {
  const r = announceRuleOf(n.type);
  if (!r) return null;
  return speechFor(r.lead, n, opts);
}

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/** Ghép "<brand>. <Anh Nam ơi, ><câu dẫn>. <tiêu đề>. <chi tiết>." – bỏ chi tiết trùng tiêu đề. */
export function speechFor(lead: string, n: { title: string; message?: string | null }, opts: SpeechOpts = {}): string {
  const brand = opts.brand ?? 'Minh Tiến';
  const who = callName(opts.listener);
  const title = cleanForSpeech(n.title);
  const detail = n.message ? cleanForSpeech(n.message) : '';
  const head = lead ? (who ? `${who} ơi, ${lowerFirst(lead)}. ` : `${lead}. `) : who ? `${who} ơi. ` : '';
  const body = detail && !title.includes(detail) ? `${title}. ${detail}` : title;
  return cleanForSpeech(`${brand}. ${head}${body}`).replace(/[.,;:\s]*$/, '') + '.';
}

/**
 * Tiêu đề thông báo đẩy trên màn khóa (iPhone không đọc to được khi khóa máy): mở đầu bằng đúng việc cần làm,
 * vd "Trả xe: Xe 61H00609 đã về bãi…". Loại không thuộc âm báo, hoặc tiêu đề đã mở đầu bằng việc đó, thì giữ nguyên.
 */
export function pushTitleFor(n: { type: string; title: string; level?: string }): string {
  const lead = announceRuleOf(n.type)?.lead ?? (n.level === 'urgent' ? 'Việc khẩn' : '');
  if (!lead || n.title.toLowerCase().startsWith(lead.toLowerCase())) return n.title;
  return `${lead}: ${n.title}`;
}

// ---------------------------------------------------------------- Giữ màn hình sáng để nghe đọc (DEC-671)
/**
 * Anh Tiến 02/11/2026: "thời gian giữ sáng ứng dụng cho từng đối tượng để đọc được":
 *  - Nhân viên (trừ tài xế và công trình): 07:00–07:30, 10:00–11:00, 16:50–17:15.
 *  - Tài xế: cắm sạc thì giữ sáng; ngoài ra các khung trên + 21:00, 22:00 (mốc xe về bãi, mỗi mốc 15 phút).
 *  - Công trình: theo khung nhân viên + 14:00–14:30 (anh Tiến 02/11/2026 "Công trình theo khung nhân viên bổ sung thêm 14h–14h30").
 * Giờ VN, dạng [bắt đầu, kết thúc) "HH:mm".
 */
export type WakeGroup = 'staff' | 'driver' | 'construction';
const STAFF_WINDOWS: ReadonlyArray<readonly [string, string]> = [
  ['07:00', '07:30'],
  ['10:00', '11:00'],
  ['16:50', '17:15'],
];
export const WAKE_WINDOWS: Record<WakeGroup, ReadonlyArray<readonly [string, string]>> = {
  staff: STAFF_WINDOWS,
  driver: [...STAFF_WINDOWS, ['21:00', '21:15'], ['22:00', '22:15']],
  construction: [...STAFF_WINDOWS, ['14:00', '14:30']],
};
export const WAKE_GROUP_LABELS: Record<WakeGroup, string> = { staff: 'Nhân viên', driver: 'Tài xế', construction: 'Công trình' };

/** Nhóm giữ sáng theo nhóm công (hồ sơ nhân viên) hoặc tài khoản chỉ dùng màn Tài xế. */
export function wakeGroupOf(workGroup: string | null | undefined, driverOnly = false): WakeGroup {
  if (driverOnly || workGroup === 'driver') return 'driver';
  if (workGroup === 'construction') return 'construction';
  return 'staff';
}

const toMin = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/** Phút trong ngày theo giờ VN (UTC+7, không đổi giờ mùa hè). */
export const vnClockMinute = (d: Date) => (((d.getUTCHours() + 7) % 24) * 60 + d.getUTCMinutes());

export function inWakeWindow(group: WakeGroup, minuteOfDay: number): boolean {
  return WAKE_WINDOWS[group].some(([a, b]) => minuteOfDay >= toMin(a) && minuteOfDay < toMin(b));
}

/** Xe đã về bãi mà chưa bấm Trả xe: nhắc lại mỗi 10 phút, tối đa 12 lần (2 giờ), tới khi trả xe hoặc tới mốc nhắc lần 2. */
export const RETURN_REPEAT_MINUTES = 10;
export const RETURN_REPEAT_MAX = 12;

/** Giọng đọc âm báo (anh Tiến 03/11/2026 "đổi được giọng"): giọng của máy (miễn phí) hoặc giọng FPT.AI đọc qua máy chủ. */
export const ANNOUNCE_FPT_VOICES = ['kim_ngan', 'gia_huy', 'ha_tieu_mai', 'ban_mai', 'thu_minh', 'le_minh', 'ngoc_lam'] as const;
export type AnnounceFptVoice = (typeof ANNOUNCE_FPT_VOICES)[number];
export type AnnounceVoice = 'device' | AnnounceFptVoice;
export const ANNOUNCE_VOICE_LABELS: Record<AnnounceVoice, string> = {
  device: 'Giọng của máy (miễn phí)',
  kim_ngan: 'FPT Kim Ngân – nữ miền Nam',
  gia_huy: 'FPT Gia Huy – nam miền Nam',
  ha_tieu_mai: 'FPT Hạ Tiểu Mai – nữ miền Nam',
  ban_mai: 'FPT Ban Mai – nữ miền Bắc',
  thu_minh: 'FPT Thu Minh – nữ miền Bắc',
  le_minh: 'FPT Lê Minh – nam miền Bắc',
  ngoc_lam: 'FPT Ngọc Lam – nữ miền Trung',
};
